<template>
  <div class="kepler-viz-3d">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>dA/dt = Const$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 渲染区 -->
      <div ref="canvasContainer" class="canvas-3d-wrapper">
         <div class="hud-top">
            <div class="item">
               <label>运行阶段</label>
               <span :class="phase">{{ phase === 'perihelion' ? '近日点 (高速)' : '远日点 (低速)' }}</span>
            </div>
            <div class="item area">
               <label>扫过面积</label>
               <span>{{ sweepArea.toFixed(1) }} units²</span>
            </div>
         </div>
      </div>

      <!-- 控制区 -->
      <div class="bottom-controls">
         <div class="control-card glass">
            <div class="row">
               <div class="col">
                  <label>轨道离心率 (e): {{ eccentricity }}</label>
                  <el-slider v-model="eccentricity" :min="0" :max="0.8" :step="0.05" size="mini"></el-slider>
               </div>
               <div class="col bottom-align">
                  <el-button type="primary" size="mini" @click="reset">重置观察时刻</el-button>
               </div>
            </div>
         </div>

         <div class="hint-card-pro">
            <i class="el-icon-guide"></i>
            <p><strong>第二定律演示：</strong>太阳位于椭圆的一个焦点。行星在相等时间内扫过的面积相等。观察近日点时速度变快，远日点变慢。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'KeplerLawViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      eccentricity: 0.6,
      time: 0,
      running: true,
      sweepArea: 50
    }
  },
  computed: {
    phase() {
      const angle = this.time % (Math.PI * 2);
      return (angle < Math.PI / 2 || angle > 1.5 * Math.PI) ? 'perihelion' : 'aphelion';
    }
  },
  mounted() {
    this.initThree();
  },
  beforeDestroy() {
    this.running = false;
    if (this.renderer) {
      this.renderer.dispose();
      this.canvasContainer.removeChild(this.renderer.domElement);
    }
  },
  watch: { eccentricity() { this.updateOrbit(); } },
  methods: {
    initThree() {
      this.canvasContainer = this.$refs.canvasContainer;
      const width = this.canvasContainer.clientWidth;
      const height = this.canvasContainer.clientHeight;

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
      this.camera.position.set(0, 100, 100);
      this.camera.lookAt(0, 0, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.canvasContainer.appendChild(this.renderer.domElement);

      // Sun (At focus)
      const sunGeo = new THREE.SphereGeometry(8, 32, 32);
      const sunMat = new THREE.MeshPhongMaterial({ color: 0xffaa00, emissive: 0xff4400, emissiveIntensity: 1 });
      this.sun = new THREE.Mesh(sunGeo, sunMat);
      this.scene.add(this.sun);

      // Planet
      this.planet = new THREE.Mesh(new THREE.SphereGeometry(3, 16, 16), new THREE.MeshPhongMaterial({ color: 0x00d2ff }));
      this.scene.add(this.planet);

      // Area Visualizer (Fan shape)
      const fanGeo = new THREE.BufferGeometry();
      const fanMat = new THREE.MeshBasicMaterial({ color: 0x00d2ff, transparent: true, opacity: 0.3, side: THREE.DoubleSide });
      this.fan = new THREE.Mesh(fanGeo, fanMat);
      this.scene.add(this.fan);

      // Lights
      this.scene.add(new THREE.AmbientLight(0xffffff, 0.3));
      const light = new THREE.PointLight(0xffffff, 1, 500);
      light.position.set(0, 50, 0);
      this.scene.add(light);

      this.updateOrbit();
      this.animate();
    },
    updateOrbit() {
      if (this.orbitLine) this.scene.remove(this.orbitLine);
      const a = 60;
      const e = this.eccentricity;
      const b = a * Math.sqrt(1 - e*e);
      const focusOffset = a * e;

      // Draw Orbit
      const curve = new THREE.EllipseCurve(-focusOffset, 0, a, b, 0, 2 * Math.PI, false, 0);
      const points = curve.getPoints(128);
      const geo = new THREE.BufferGeometry().setFromPoints(points);
      const mat = new THREE.LineBasicMaterial({ color: 0x444, transparent: true, opacity: 0.3 });
      this.orbitLine = new THREE.Line(geo, mat);
      this.orbitLine.rotation.x = Math.PI / 2;
      this.scene.add(this.orbitLine);
      
      this.sun.position.x = 0; // Fixed at origin focus
    },
    reset() { this.time = 0; },
    animate() {
      if (!this.running) return;
      requestAnimationFrame(this.animate);

      // Keplerian motion: Sweep equal area
      // In polar: r^2 * dtheta/dt = Const. So dtheta = dt / r^2
      const a = 60;
      const e = this.eccentricity;
      const angle = this.time % (Math.PI * 2);
      const r = (a * (1 - e * e)) / (1 + e * Math.cos(angle));
      
      const speedScale = 1000 / (r * r); // The dynamic speed multiplier
      this.time += 0.05 * speedScale;

      this.planet.position.x = r * Math.cos(angle);
      this.planet.position.z = r * Math.sin(angle);

      // Update Sweep Area Geometry
      const vertices = [0, 0, 0];
      for(let i=0; i<30; i++) {
          const t_off = angle - (i * 0.02);
          const r_off = (a * (1 - e * e)) / (1 + e * Math.cos(t_off));
          vertices.push(r_off * Math.cos(t_off), 0, r_off * Math.sin(t_off));
      }
      this.fan.geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
      this.fan.geometry.getAttribute('position').needsUpdate = true;

      this.renderer.render(this.scene, this.camera);
    }
  }
}
</script>

<style scoped lang="scss">
.kepler-viz-3d {
  flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden;
}

.viz-header {
  display: flex; justify-content: space-between; align-items: center;
  .title { font-size: 1.1rem; color: #fff; font-weight: 300; }
  .formula-chip { padding: 4px 12px; background: rgba(0, 210, 255, 0.1); border-radius: 8px; color: #00d2ff; font-family: serif; font-weight: bold; }
}

.canvas-3d-wrapper {
  flex: 1.5; background: #000; border-radius: 12px; position: relative; overflow: hidden;
  .hud-top {
     position: absolute; top: 15px; left: 15px; display: flex; gap: 15px;
     .item {
        background: rgba(10, 10, 20, 0.7); backdrop-filter: blur(5px); border-left: 3px solid #00d2ff; padding: 5px 15px; border-radius: 8px;
        label { display: block; font-size: 0.55rem; color: #888; }
        span { font-size: 0.9rem; color: #fff; font-family: monospace; }
        .perihelion { color: #ff6b6b; font-weight: bold; }
        .aphelion { color: #2ed573; font-weight: bold; }
     }
  }
}

.bottom-controls {
  flex: 0.5; display: flex; flex-direction: column; gap: 10px; overflow-y: auto; padding-bottom: 5px;
}

.control-card {
  background: rgba(255, 255, 255, 0.03); border-radius: 12px; padding: 12px 18px;
  .row { display: grid; grid-template-columns: 1fr 100px; gap: 20px; align-items: center; }
  .col { label { display: block; font-size: 0.75rem; color: #aaa; margin-bottom: 5px; } }
}

.hint-card-pro {
   background: rgba(0, 210, 255, 0.05); border-radius: 12px; padding: 12px 15px; display: flex; align-items: flex-start; gap: 12px;
   i { color: #00d2ff; font-size: 1rem; margin-top: 2px; }
   p { margin: 0; font-size: 0.75rem; color: #888; line-height: 1.5; strong { color: #fff; } }
}
</style>
